import React from 'react';
import { Box, Text, VStack, Flex, Link, Icon } from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import ModalShell from '../../../components/ui/ModalShell';
import { isVideoUrl } from '../../../utils/fileUtils';
import deliverableNames from '../../../utils/deliverableNames';
import { ff } from '../../../theme';
import { SubmissionSheetTitle, StatusPill, InfoRows } from './submissionSheetParts';

interface PendingReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Single asset URL — used when only one item is under review. */
  contentUrl?: string;
  /** Multiple asset URLs — renders a horizontally scrollable row (e.g. carousels). */
  contentUrls?: string[];
  contentType: 'link' | 'file';
  reviewStage: 'brand' | 'foodfluence';
  deliverableKey: string;
  submittedAt?: Date | null;
}

const formatSubmittedDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) +
  ' at ' +
  date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const UnderReviewBadge: React.FC = () => <StatusPill tone="grey">Under review</StatusPill>;

const FilePreview: React.FC<{ url: string; index?: number; fillWidth?: boolean }> = ({
  url,
  index,
  fillWidth = false,
}) => {
  const isVideo = isVideoUrl(url);

  return (
    <Box
      position="relative"
      bg={ff.ink}
      borderRadius="18px"
      overflow="hidden"
      flexShrink={0}
      w={fillWidth ? '100%' : '72%'}
      maxW={fillWidth ? undefined : '320px'}
      sx={{ scrollSnapAlign: 'start' }}
    >
      {isVideo ? (
        <video
          controls
          src={url}
          style={{
            width: '100%',
            maxHeight: '55vh',
            display: 'block',
            objectFit: 'contain',
          }}
          preload="metadata"
        />
      ) : (
        <img
          src={url}
          alt={index !== undefined ? `Submitted content ${index + 1}` : 'Submitted content'}
          style={{
            width: '100%',
            maxHeight: '55vh',
            display: 'block',
            objectFit: 'contain',
          }}
        />
      )}
      {index !== undefined && (
        <Flex
          position="absolute"
          top="8px"
          left="8px"
          minW="26px"
          h="26px"
          px="6px"
          borderRadius="full"
          bg={ff.ink}
          align="center"
          justify="center"
        >
          <Text fontSize="13px" fontWeight={600} color={ff.lime} lineHeight={1}>
            {index + 1}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

const PendingReviewModal: React.FC<PendingReviewModalProps> = ({
  isOpen,
  onClose,
  contentUrl,
  contentUrls,
  contentType,
  reviewStage,
  deliverableKey,
  submittedAt,
}) => {
  const urls = contentUrls?.length ? contentUrls : contentUrl ? [contentUrl] : [];
  const isMultiFile = contentType === 'file' && urls.length > 1;

  const deliverableName =
    deliverableNames[deliverableKey as keyof typeof deliverableNames]?.singular || deliverableKey;

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} size={isMultiFile ? 'lg' : 'md'}>
      <VStack spacing="16px" align="stretch" w="100%">
        <SubmissionSheetTitle
          title={`Under ${reviewStage === 'brand' ? 'brand' : 'FoodFluence'} review`}
          context={deliverableName || undefined}
        />

        {contentType === 'file' ? (
          isMultiFile ? (
            <Box>
              <Flex pb="10px" justify="space-between" align="center" gap={3}>
                <Text fontSize="12px" fontWeight={600} color="#55555A" letterSpacing="0.08em" textTransform="uppercase">
                  Draft submission · {urls.length} items
                </Text>
                <UnderReviewBadge />
              </Flex>
              <Flex
                overflowX="auto"
                gap="10px"
                sx={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                {urls.map((url, index) => (
                  <FilePreview key={`${url}-${index}`} url={url} index={index} />
                ))}
              </Flex>
            </Box>
          ) : (
            <Box position="relative">
              {urls[0] && <FilePreview url={urls[0]} fillWidth />}
              <Box position="absolute" top="12px" right="12px">
                <UnderReviewBadge />
              </Box>
            </Box>
          )
        ) : (
          <Box>
            <Flex justify="space-between" align="center" mb="8px" gap={3}>
              <Text fontSize="14px" fontWeight={600}>
                Submitted link
              </Text>
              <UnderReviewBadge />
            </Flex>
            <Link
              href={urls[0]}
              isExternal
              color={ff.ink}
              fontSize="14px"
              fontWeight={600}
              textDecoration="underline"
              wordBreak="break-all"
              display="flex"
              alignItems="flex-start"
              gap={2}
              px="14px"
              py="12px"
              borderRadius="14px"
              border={`1px solid ${ff.border}`}
            >
              {urls[0]}
              <Icon as={ExternalLinkIcon} boxSize={3} mt={0.5} flexShrink={0} />
            </Link>
          </Box>
        )}

        {reviewStage === 'brand' && (
          <>
            {submittedAt && <InfoRows rows={[{ k: 'Submitted', v: formatSubmittedDate(submittedAt) }]} />}
            <Text fontSize="14px" lineHeight={1.4} color={ff.body}>
              The brand is reviewing your content. You'll be notified when it's approved or if edits are needed.
            </Text>
          </>
        )}
      </VStack>
    </ModalShell>
  );
};

export default PendingReviewModal;
